'use client';
import { useEffect, useMemo, useState } from 'react';
import OHCContext from './OHCContext';
import { useDesktopMode } from '@/hooks/useDesktopMode';
import useAuthentication from '@/hooks/useAuthentication';
import retrieveUserData from '@/actions/retrieve-user';
import useEnv from '@/hooks/useEnv';
import isEmpty from 'lodash/isEmpty';

interface PageProps {
  children: React.ReactNode;
  helpTopics: any[];
  isMobileAppView: boolean;
  headersList: any;
  cookieList: any;
  popularTopics: any;
  categoryIds: any;
}

const moreHelpLinks = [
  { id: 'shipping-and-tracking/tracking-the-status-of-an-order', title: `Tracking the status of an order`, icon: 'GreenIcon' },
  { id: 'shipping-and-tracking/international-shipments', title: `International shipping`, icon: 'GreenIcon' },
  { id: 'shipping-and-tracking/changing-your-primary-shipping-address', title: `Changing a shipping address`, icon: 'GreenIcon' }
];

const OHCContextProvider = ({ children, helpTopics, isMobileAppView, headersList, cookieList, popularTopics, categoryIds }: PageProps) => {
  const [error, setError] = useState<any>(null);
  const [envVars, setEnvVars] = useState<any>(null);
  const { chewyBaseUrl } = useEnv();

  // Initialize exploreChewyLinks with runtime config
  const exploreChewyLinks = useMemo(
    () =>
      isEmpty(chewyBaseUrl)
        ? []
        : [
            { id: '1', title: `Today's deals`, link: `${chewyBaseUrl}/deals/todays-deals-2723`, icon: 'TagIcon' },
            { id: '2', title: `Get prescription items delivered fast`, link: `${chewyBaseUrl}/b/pharmacy-15697`, icon: 'PrescriptionsIcon' },
            { id: '3', title: `Chat free with our licensed vet team`, link: `${chewyBaseUrl}/b/connect-vet-16616`, icon: 'VetChatIcon' },
            {
              id: '4',
              title: 'Get free shipping and rewards on every order with Chewy+',
              link: `${chewyBaseUrl}/app/membership`,
              icon: 'LoyaltyIcon'
            }
          ],
    [chewyBaseUrl]
  );

  // Add all required state variables
  const [navData, setNavData] = useState<any>(helpTopics || []);
  const [selectedTopic, setSelectedTopic] = useState<any>(null);
  const [selectedSection, setSelectedSection] = useState<any>(null);
  const [selectedArticle, setSelectedArticle] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentTopicId, setCurrentTopicId] = useState<any>(null);
  const [isHelpResults, setIsHelpResults] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [sectionsData, setSectionsData] = useState<any[]>([]);
  const [moreHelpLinksData, setMoreHelpLinksData] = useState<any[]>(moreHelpLinks);
  const [exploreChewyLinksData, setExploreChewyLinksData] = useState<any[]>(exploreChewyLinks);
  const [maxCategoriesShown, setMaxCategoriesShown] = useState<number>(3);
  const [isResultsLoading, setIsResultsLoading] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<any>(null);
  const [isMobileApp] = useState<boolean>(isMobileAppView);
  const isDesktopMode = useDesktopMode();
  const { authToken, setAuthToken, isAuthenticated, setHeaders, setCookies, headers, cookies } = useAuthentication();
  const [customer, setCustomer] = useState<any>(null);
  const [categoryIdsMap, setCategoryIdsMap] = useState<any>(categoryIds);
  const [isNavError, setIsNavError] = useState<boolean>(false);

  useEffect(() => {
    const getCustomerData = async () => {
      try {
        const customerData = await retrieveUserData();
        console.log('************ OHC customer data ************');

        if (!('error' in customerData)) {
          setCustomer(customerData);
        } else {
          console.log('Error getting customer data', customerData.error);
          setCustomer(null);
        }
      } catch (error) {
        console.log('Error getting customer data', error);
      }
    };
    if (cookieList) {
      getCustomerData();
    }
  }, [setCustomer, cookieList]);

  useEffect(() => {
    setHeaders(headersList);
    setCookies(cookieList);
  }, [headersList, cookieList, setHeaders, setCookies]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const search = params.get('searchQuery') || '';
    setSearchQuery(search);
  }, []);

  if (error) {
    return <div>Error Env Provider</div>;
  }

  return (
    <OHCContext.Provider
      value={{
        navData,
        setNavData,
        selectedTopic,
        setSelectedTopic,
        selectedSection,
        setSelectedSection,
        selectedArticle,
        setSelectedArticle,
        searchQuery,
        setSearchQuery,
        currentTopicId,
        setCurrentTopicId,
        isHelpResults,
        setIsHelpResults,
        loading,
        setLoading,
        popularTopics: popularTopics,
        sectionsData,
        setSectionsData,
        // setPopularTopics,
        moreHelpLinksData,
        setMoreHelpLinksData,
        exploreChewyLinksData,
        setExploreChewyLinksData,
        error,
        setError,
        envVars,
        setEnvVars,
        maxCategoriesShown,
        setMaxCategoriesShown,
        isDesktopMode,
        isMobileApp,
        isResultsLoading,
        setIsResultsLoading,
        searchResults,
        setSearchResults,
        authToken,
        setAuthToken,
        isAuthenticated,
        setHeaders,
        setCookies,
        headers,
        cookies,
        customer,
        categoryIdsMap,
        isNavError,
        setIsNavError
      }}
    >
      {children}
    </OHCContext.Provider>
  );
};

export default OHCContextProvider;
